import { Theme, Typography, useMediaQuery } from '@mui/material';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from 'assets';

import styles from './RegisterLayout.module.scss';
import image from '../../assets/images/RegistrationLeftImage.png';
import { isHipaaLink } from '../../config/productProfile';

import maleImg from '../../assets/images/director.webp';
import girlImg from '../../assets/images/therapist.webp';

export type LeftSideType = '30DaysFree' | 'promo' | 'benefits';

type RegisterLayoutProps = {
  children: JSX.Element;
  leftSideType: LeftSideType;
  headers: string[];
  selectedHeader: number;
  leftSide?: JSX.Element | null;
  hideLeftSide?: boolean;
};

export function RegisterLayout({
  children,
  leftSide,
  leftSideType,
  headers,
  selectedHeader,
  hideLeftSide,
}: RegisterLayoutProps) {
  const { t } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const benefitItems = Array.from({ length: 13 }, (_, index) => t(`benefitItem${index + 1}`));

  return (
    <div className={styles.main}>
      <div className={styles.container}>
        <div className={styles.header}>
          {headers.map((title: string, index: number) => (
            <div
              key={index}
              className={classnames(styles.titleContainer, {
                [styles.titleContainerSelected]: selectedHeader > index,
                [styles.titleContainerNextSelected]: selectedHeader > index + 1 || selectedHeader === 3,
              })}
            >
              <div className={styles.titleContainerRound}>
                {!isMobile && <div className={styles.number}>{index + 1}</div>}
                <Typography variant={!isMobile ? 'body4' : 'body1'}>{title}</Typography>
              </div>
            </div>
          ))}
        </div>

        {/* ✅ Hide Left Side on Mobile */}
        {!isMobile && !hideLeftSide && leftSideType === 'benefits' && (
          <div className={styles.leftSideBenefits}>
            <img src={image} alt='Registration' />

            <Typography variant='h2' component='h2'>
              {t('welcome')}
            </Typography>

            <Typography variant='h2' component='h2'>
              <>
                {t('benefitsOfHipaa')}
                <br />
                {isHipaaLink ? (
                  <>
                    HIPAA<span className={styles.link}>LINK</span>
                  </>
                ) : (
                  <>
                    <strong>VIA3</strong>
                  </>
                )}
              </>
            </Typography>

            <ul className={styles.dataText}>
              {benefitItems.map((dataTextItem, index) => (
                <li key={index}>
                  <span className={styles.dataCheckIcon}>
                    <CheckIcon />
                  </span>

                  <Typography variant='body5' component='span'>
                    {dataTextItem}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isMobile && !hideLeftSide && leftSideType === '30DaysFree' && (
          <div className={styles.leftSide30DaysFree}>
            <Typography variant='h2' component='h2'>
              {t('getStartedWithYour')}
            </Typography>

            <Typography
              variant='h1'
              component='h1'
              sx={{
                margin: { xs: '-6px', sm: '-10px', md: '-12px' }, // Adjusts for mobile, tablet, and desktop
              }}
            >
              {t('freeTrialToday')}
            </Typography>

            <div className={styles.box}>
              <img
                src={isHipaaLink ? girlImg : maleImg}
                className={isHipaaLink ? styles.girl : styles.male}
                alt='User Testimonial'
              />

              <div className={styles.texts}>
                <Typography variant='body2' component='span' className={styles.comment}>
                  {isHipaaLink ? `"${t('registerLayoutTestimonialHipaa')}"` : `"${t('registerLayoutTestimonialVia3')}"`}
                </Typography>

                <Typography variant='body4' component='span'>
                  {isHipaaLink ? t('registerLayoutTestimonialHipaaName') : t('registerLayoutTestimonialVia3Name')}
                </Typography>

                <Typography variant='body4' component='span' color='var(--app-brand-primary)'>
                  {isHipaaLink ? t('registerLayoutTestimonialHipaaRole') : t('registerLayoutTestimonialVia3Role')}
                </Typography>
              </div>
            </div>
          </div>
        )}

        {!isMobile && !hideLeftSide && leftSideType === 'promo' && leftSide}

        <div className={styles.rightSide}>{children}</div>
      </div>
    </div>
  );
}
